import {
  BoxGeometry,
  CircleGeometry,
  CylinderGeometry,
  LatheGeometry,
  SphereGeometry,
  TorusGeometry,
  Vector2,
} from 'three';
import { mergeParts } from './merge.js';

/**
 * A refractor on an altazimuth yoke, the kind a city would be famous for.
 *
 * Local frame: the instrument sights along **-Z**. The eyepiece is just
 * behind the origin and the objective is out at z = -1.6, so a camera sitting
 * at the origin with no rotation (and therefore looking down its own -Z) is
 * looking through the tube the right way round. The mount stands on y = -1.5,
 * which is where the tread is.
 */

/**
 * Profile of the draw tube, revolved: [radius, z].
 *
 * It deliberately never reaches radius 0 at either end: a lathe that closes
 * puts a brass disc across the eyepiece and another across the objective,
 * and the view through the instrument becomes a view of the inside of a lid.
 */
const TUBE_PROFILE = [
  [0.055, -0.02],
  [0.062, 0.02],
  [0.05, 0.06],
  [0.052, 0.2],
  [0.072, 0.22],
  [0.072, 0.3],
  [0.095, 0.33],
  [0.095, 1.25],
  [0.112, 1.29],
  [0.126, 1.34],
  [0.126, 1.52],
  [0.118, 1.56],
  [0.118, 1.6],
];

function tubeGeometry() {
  const pts = TUBE_PROFILE.map(([r, z]) => new Vector2(r, z));
  const lathe = new LatheGeometry(pts, 40);
  // LatheGeometry revolves around +Y; stand it up along +Z.
  lathe.rotateX(Math.PI / 2);
  lathe.scale(1, 1, -1);
  lathe.computeVertexNormals();
  return lathe;
}

function ringsGeometry() {
  const parts = [];
  for (const z of [0.52, 1.04]) {
    const ring = new TorusGeometry(0.108, 0.016, 10, 28);
    ring.translate(0, 0, -z);
    parts.push(ring);
  }
  // Finder scope riding alongside.
  const finder = new CylinderGeometry(0.022, 0.026, 0.42, 14);
  finder.rotateX(Math.PI / 2);
  finder.translate(0.14, 0.03, -0.52);
  parts.push(finder);
  const bracket = new BoxGeometry(0.1, 0.022, 0.03);
  bracket.translate(0.095, 0.03, -0.4);
  parts.push(bracket);
  return mergeParts(parts);
}

function mountGeometry() {
  const parts = [];

  // Yoke arms gripping the tube at its balance point.
  for (const side of [-1, 1]) {
    const arm = new BoxGeometry(0.036, 0.46, 0.08);
    arm.translate(side * 0.16, -0.23, -0.72);
    parts.push(arm);
    const boss = new CylinderGeometry(0.05, 0.05, 0.04, 14);
    boss.rotateZ(Math.PI / 2);
    boss.translate(side * 0.155, 0.0, -0.72);
    parts.push(boss);
  }

  // Column and base.
  const column = new CylinderGeometry(0.052, 0.072, 0.78, 16);
  column.translate(0, -0.84, -0.72);
  parts.push(column);
  const collar = new TorusGeometry(0.07, 0.015, 8, 20);
  collar.rotateX(Math.PI / 2);
  collar.translate(0, -0.47, -0.72);
  parts.push(collar);

  // Three legs, splayed, their feet on y = -1.5.
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    const leg = new CylinderGeometry(0.024, 0.034, 1.08, 10);
    leg.rotateX(Math.cos(a) * 0.26);
    leg.rotateZ(-Math.sin(a) * 0.26);
    leg.translate(Math.sin(a) * 0.19, -0.95, -0.72 + Math.cos(a) * 0.19);
    parts.push(leg);
  }

  // Slow-motion knob — the small human detail that sells the scale.
  const knob = new SphereGeometry(0.03, 12, 10);
  knob.translate(0.2, -0.3, -0.62);
  parts.push(knob);
  const rod = new CylinderGeometry(0.008, 0.008, 0.2, 8);
  rod.rotateZ(Math.PI / 2.4);
  rod.translate(0.12, -0.23, -0.65);
  parts.push(rod);

  return mergeParts(parts);
}

/** The objective lens: a disc that catches whatever light is left. */
function lensGeometry() {
  const lens = new CircleGeometry(0.113, 36);
  lens.translate(0, 0, -1.555);
  return lens;
}

/**
 * @returns {{brass: import('three').BufferGeometry,
 *            iron: import('three').BufferGeometry,
 *            lens: import('three').BufferGeometry}}
 */
export function createTelescopeGeometry() {
  const brass = mergeParts([tubeGeometry(), ringsGeometry()]);
  brass.computeVertexNormals();
  const iron = mountGeometry();
  const lens = lensGeometry();
  for (const g of [brass, iron, lens]) g.computeBoundingSphere();
  return { brass, iron, lens };
}
